import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { langField } from "../lib/i18n";
import { fetchTags } from "../lib/api";

// Cache tag metadata once per page load so each card doesn't re-fetch.
let _tagCache = null;
let _tagPromise = null;
function getTagsCached() {
  if (_tagCache) return Promise.resolve(_tagCache);
  if (!_tagPromise) {
    _tagPromise = fetchTags()
      .then((d) => {
        _tagCache = d.tags || [];
        return _tagCache;
      })
      .catch(() => []);
  }
  return _tagPromise;
}

const COLOR_MAP = {
  yellow: "bg-kult-yellow text-black",
  pink: "bg-kult-pink text-black",
  white: "bg-white text-black",
  black: "bg-black text-white border border-white",
};

export const ProductCard = ({ product }) => {
  const { lang, formatPrice } = useApp();
  const [hover, setHover] = useState(false);
  const [tagsMeta, setTagsMeta] = useState([]);

  useEffect(() => {
    getTagsCached().then(setTagsMeta);
  }, []);

  // Front = first image; back = second image (shown on hover).
  const front = product.images?.[0];
  const back = product.images?.[1];
  const img = hover && back ? back : front;
  const name = langField(product, "name", lang);

  // Resolve product.tags (slugs) → tag metadata; fall back gracefully.
  const tagSlugs = Array.isArray(product.tags) ? product.tags : [];
  const resolvedTags = tagSlugs
    .map((slug) => tagsMeta.find((t) => t.slug === slug))
    .filter(Boolean)
    .slice(0, 3); // Cap visible tags to keep card clean.

  return (
    <Link
      to={`/products/${product.slug}`}
      className="group block border border-kult-border bg-black hover:border-kult-pink transition-colors"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        {img && (
          <img
            src={img}
            alt={name}
            className="w-full h-full object-contain transition-all duration-500 group-hover:scale-[1.04]"
            data-testid={`product-image-${product.slug}`}
          />
        )}

        {/* Legacy single badge (kept for backwards-compat) */}
        {product.badge && (
          <span
            className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] px-2 py-1 bg-kult-yellow text-black"
            data-testid={`product-badge-${product.slug}`}
          >
            {langField(product, "badge", lang)}
          </span>
        )}

        {/* Multi-tag chips (top-right) */}
        {resolvedTags.length > 0 && (
          <div className="absolute top-3 right-3 flex flex-col items-end gap-1" data-testid={`product-tags-${product.slug}`}>
            {resolvedTags.map((tag) => (
              <span
                key={tag.slug}
                className={`font-mono text-[10px] tracking-[0.25em] px-2 py-1 ${COLOR_MAP[tag.color] || COLOR_MAP.yellow}`}
                data-testid={`product-tag-${product.slug}-${tag.slug}`}
              >
                {lang === "pt" ? tag.label_pt : tag.label_en}
              </span>
            ))}
          </div>
        )}

        {/* Subtle "FRONT / BACK ⇄" indicator when a back image exists */}
        {back && (
          <span
            className="absolute bottom-3 right-3 font-mono text-[9px] tracking-[0.25em] px-2 py-1 bg-black/70 text-white border border-kult-border opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`product-flip-hint-${product.slug}`}
          >
            ⇄ {hover ? (lang === "pt" ? "VERSO" : "BACK") : (lang === "pt" ? "FRENTE" : "FRONT")}
          </span>
        )}
      </div>
      <div className="flex items-start justify-between p-4 border-t border-kult-border gap-3">
        <h3 className="font-display text-lg leading-tight text-white pr-2">{name}</h3>
        <span
          className="font-mono text-sm bg-kult-yellow text-black px-2 py-0.5 whitespace-nowrap"
          data-testid={`product-price-${product.slug}`}
        >
          {formatPrice(product.price, { decimals: 0 })}
        </span>
      </div>
    </Link>
  );
};
